import { useState, useEffect } from 'react';
import { RefreshCw, Download, FileJson, Clock, Hash, Layers } from 'lucide-react';
import { protocolService } from '@/services/api';
import { Manifest } from '@/types';

export default function ManifestPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTherapyClass, setSelectedTherapyClass] = useState<string | null>(null);

  useEffect(() => {
    fetchManifest();
  }, []);

  const fetchManifest = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await protocolService.getManifest();
      setManifest(data);
    } catch (err) {
      setError('Failed to fetch manifest');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm('Are you sure you want to regenerate the manifest? This will rebuild it from all approved protocols.')) {
      return;
    }

    try {
      setRegenerating(true);
      setError(null);
      await protocolService.regenerateManifest();
      await fetchManifest();
    } catch (err) {
      setError('Failed to regenerate manifest');
      console.error(err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownload = () => {
    if (!manifest) return;

    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manifest-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const therapyClasses = manifest?.by_therapy_class ? Object.keys(manifest.by_therapy_class).sort() : [];
  const totalProtocols = manifest?.protocols?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Protocol Manifest</h1>
          <p className="text-gray-600 mt-1">
            View and manage the protocol manifest used by the AI recommendation engine
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleDownload}
            disabled={!manifest}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download JSON
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regenerating...' : 'Regenerate Manifest'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileJson className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Protocols</p>
              <p className="text-2xl font-bold text-gray-900">{totalProtocols}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Layers className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Therapy Classes</p>
              <p className="text-2xl font-bold text-gray-900">{therapyClasses.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Hash className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Manifest Version</p>
              <p className="text-sm font-mono text-gray-900 truncate">
                {manifest?.version?.split('T')[0] || 'N/A'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="text-sm text-gray-900">
                {manifest?.version
                  ? new Date(manifest.version).toLocaleString()
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Therapy Classes */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Protocols by Therapy Class</h2>
        </div>
        <div className="divide-y">
          {therapyClasses.map((therapyClass) => {
            const protocols = manifest?.by_therapy_class?.[therapyClass] || [];
            const isExpanded = selectedTherapyClass === therapyClass;

            return (
              <div key={therapyClass}>
                <button
                  onClick={() => setSelectedTherapyClass(isExpanded ? null : therapyClass)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                      {protocols.length}
                    </span>
                    <span className="font-medium text-gray-900">{therapyClass}</span>
                  </div>
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="py-2 font-medium">Protocol ID</th>
                          <th className="py-2 font-medium">Display Name</th>
                          <th className="py-2 font-medium">Type</th>
                          <th className="py-2 font-medium">S3 Path</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {protocols.map((protocol) => (
                          <tr key={protocol.protocol_id}>
                            <td className="py-2 font-mono text-xs">{protocol.protocol_id}</td>
                            <td className="py-2">{protocol.display_name}</td>
                            <td className="py-2">{protocol.protocol_type}</td>
                            <td className="py-2 font-mono text-xs text-gray-500 truncate max-w-xs">
                              {protocol.s3_key}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          {therapyClasses.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">
              No approved protocols in manifest. Approve some protocols to populate the manifest.
            </div>
          )}
        </div>
      </div>

      {/* Raw JSON Preview */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Raw Manifest JSON</h2>
        </div>
        <div className="p-4">
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-96 text-xs">
            {manifest ? JSON.stringify(manifest, null, 2) : 'No manifest data'}
          </pre>
        </div>
      </div>
    </div>
  );
}
